import {Link, useNavigate} from '@shopify/hydrogen';

import {Index, useHits, UseHitsProps, Configure} from 'react-instantsearch';

import {Button} from '~/components/elements';
import {SearchAutocompleteInput} from '../elements/SearchAutocompleteInput';
import {useCallback, useState} from 'react';

import {useSearchParam} from 'react-use';
import navigation from '~/lib/navigation';
import {DATA_TEST_IDS} from '~/lib/const';

const BlogHits = ({
  handleClickViewAllButton,
  ...algoliaProps
}: {
  handleClickViewAllButton: () => void;
} & UseHitsProps) => {
  const {results} = useHits(algoliaProps);
  return (
    <Button variant={'primary'} size={'sm'} onClick={handleClickViewAllButton}>
      View Results For Chairlift Chat ({results?.nbHits})
    </Button>
  );
};

const SkiTestHits = ({
  handleClickViewAllButton,
  ...algoliaProps
}: {
  handleClickViewAllButton: () => void;
} & UseHitsProps) => {
  const {results} = useHits(algoliaProps);
  return (
    <Button variant={'primary'} size={'sm'} onClick={handleClickViewAllButton}>
      View Results For Ski Tests ({results?.nbHits})
    </Button>
  );
};

const ProductHits = ({
  currentRefinement,
  onCloseCallback,
  handleClickViewAllButton,
  ...algoliaProps
}: {
  currentRefinement: string;
  onCloseCallback: () => void;
  handleClickViewAllButton: () => void;
} & UseHitsProps) => {
  const {hits, results} = useHits(algoliaProps);

  return (
    <>
      <span className="text-16/20 md:text-20/26 font-header font-bold tracking-wider mt-[58px]">
        Products {currentRefinement.length > 0 && `for ${currentRefinement}`}
      </span>
      {hits?.length > 0 ? (
        <ul className="max-h-96 scroll-py-3 overflow-y-auto ">
          {hits?.map((hit: any) => (
            <li
              key={hit.objectID}
              className="text-16/24 md:text-18/26 py-2 line-clamp-2 hover:underline border-b border-gray-250"
            >
              <Link
                to={navigation.productDetail.getPath(hit.handle)}
                onClick={onCloseCallback}
              >
                {hit.title}
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="font-sans text-16/24 max-w-[500px] text-start">
          No results
        </p>
      )}

      <Button
        className="mt-4"
        variant={'primary'}
        size={'sm'}
        onClick={handleClickViewAllButton}
      >
        View Results For All Products ({results?.nbHits})
      </Button>
    </>
  );
};

export const SearchAutocomplete = ({placeholder}: {placeholder?: string}) => {
  const [currentRefinement, setCurrentRefinement] = useState<string>('');

  const navigate = useNavigate();
  const [openRecentSearch, setOpenRecentSearch] = useState(false);
  const searchQuery = useSearchParam('q');

  const onFocusCallback = useCallback(() => {
    setOpenRecentSearch(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const onBlurCallback = useCallback(() => {
    setTimeout(() => {
      document.body.style.overflow = 'auto';
    }, 100);
  }, []);

  const onCloseCallback = useCallback(() => {
    setTimeout(() => {
      setOpenRecentSearch(false);
      document.body.style.overflow = 'auto';
    }, 100);
  }, []);

  const handleClickViewAllButton = useCallback(
    (tabName: string) => {
      const isSearchPage = window.location.pathname === '/search';
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('q', currentRefinement ?? '');
      searchParams.set('tab', tabName);
      if (isSearchPage) {
        navigate(window.location.pathname + `?${searchParams.toString()}`, {
          replace: true,
          scroll: false,
        });
      } else {
        navigate(`/search?${searchParams.toString()}`);
      }
      onCloseCallback();

      return false;
    },
    [navigate, onCloseCallback, currentRefinement],
  );

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      handleClickViewAllButton('products');
    },
    [handleClickViewAllButton],
  );

  const stockPolicyFilter =
    'NOT named_tags.pfs:"hidden:hide" AND (inventory_available:true OR tags:"Bundle Product")';

  return (
    <>
      <form
        className="flex items-center w-full xl:w-[748px]"
        onSubmit={handleSubmit}
      >
        <SearchAutocompleteInput
          type="search"
          placeholder={placeholder ?? 'Search'}
          name="q"
          defaultValue={searchQuery ?? ''}
          // Closing auto-complete for now because it shows other unrelated search results
          autoComplete={'off'}
          onFocus={onFocusCallback}
          onBlur={onBlurCallback}
          refine={setCurrentRefinement}
        />
      </form>

      {openRecentSearch && (
        <>
          <button
            type="button"
            className="fixed left-0 right-0 bottom-0 bg-black/80 top-[178px] xl:top-[120px] z-[9998]"
            onClick={onCloseCallback}
          />
          <div
            data-testid={DATA_TEST_IDS.SEARCH_OPENED}
            className="h-[471px] xl:h-[467px] bg-white absolute top-[130px] xl:top-[72px] left-0 right-0 z-[9999]"
          >
            <div className="flex flex-col gap-3 max-w-3xl mx-auto px-5 xl:px-0 mt-5">
              <div>
                <Index indexName="shopify_products">
                  <Configure
                    query={currentRefinement}
                    filters={stockPolicyFilter}
                    hitsPerPage={5}
                  />
                  <ProductHits
                    currentRefinement={currentRefinement}
                    onCloseCallback={onCloseCallback}
                    handleClickViewAllButton={() =>
                      handleClickViewAllButton('products')
                    }
                  />
                </Index>
              </div>
              <div>
                <Index indexName="sanity_skiTest">
                  <Configure query={currentRefinement} />
                  <SkiTestHits
                    handleClickViewAllButton={() =>
                      handleClickViewAllButton('skitest')
                    }
                  />
                </Index>
              </div>
              <div>
                <Index indexName="sanity_blog">
                  <Configure query={currentRefinement} />
                  <BlogHits
                    handleClickViewAllButton={() =>
                      handleClickViewAllButton('blog')
                    }
                  />
                </Index>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};
